import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { IoMdTrash } from 'react-icons/io';
import { GET_AIR_PRODUCTS } from '../../GraphQL/Jenkins/Querys';
import {
  ADD_AIR_PRODUCT_EXECUTION,
  REMOVE_AIR_PRODUCT_EXECUTION,
} from '../../GraphQL/Jenkins/Mutations';
import GraphQLWrapper from '../UI/GraphQLWrapper/GraphQLWrapper';
import './airProductsMenu.css';
import 'primereact/resources/primereact.min.css';
import { AIR_PRODUCT_STATUS_TO_TEXT } from '../../Constants/constants';
import CustomModal from '../UI/Modal/Modal';
import { AppContext } from '../UI/contexts';
import '../TestExecutions/TestExecutions.css';

function AirProductsMenu({ productName }) {
  const [airProduct, setAirProduct] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [showRemoveExecutionModal, setShowRemoveExecutionModal] =
    useState(false);
  const [selectedToRemove, setSelectedToRemove] = useState('');

  const { user } = useContext(AppContext);

  // Preselect the first test of every airProduct
  useEffect(() => {
    if (airProduct) {
      const initialSelections = [];

      if (
        airProduct.tests &&
        airProduct.tests.length > 0 &&
        !selectedTests.length
      ) {
        // Select the first test for each airProduct
        initialSelections.push(airProduct.tests[0].name);
      }

      if (initialSelections.length > 0) {
        setSelectedTests(initialSelections);
      }
    }
  }, [airProduct]);

  const { loading, error, refetch } = useQuery(GET_AIR_PRODUCTS, {
    variables: {
      filter: {
        name: productName,
      },
    },
    pollInterval: 15000, // Refresh data every 15 seconds
    onCompleted: (data) => {
      if (data?.airProductFindMany?.length > 0) {
        setAirProduct(data.airProductFindMany[0]);
      }
    },
  });

  // Mutation for running an air product execution
  const [addAirProductExecution] = useMutation(ADD_AIR_PRODUCT_EXECUTION, {
    onCompleted: (data) => {
      window.flash('Execution started successfully', 'success');
      // Clear selected tests for this air product
      setSelectedTests([]);
      refetch();
    },
    onError: (error, { variables }) => {
      window.flash(error.message, 'error');
    },
  });

  // Mutation for removing an air product execution
  const [removeAirProductExecution] = useMutation(
    REMOVE_AIR_PRODUCT_EXECUTION,
    {
      onCompleted: (data) => {
        window.flash('Execution removed successfully', 'success');
        refetch();
        setSelectedToRemove('');
      },
      onError: (error, { variables }) => {
        window.flash(error.message, 'error');
        setSelectedToRemove('');
      },
    }
  );

  // Handle removing an air product execution
  const handleRemoveExecution = () => {
    setShowRemoveExecutionModal(false);

    removeAirProductExecution({
      variables: {
        productId: airProduct._id,
        executions: [selectedToRemove],
      },
    });
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return 'Unknown';

    // Format specific air product statuses
    switch (status.toLowerCase()) {
      case 'to_execute':
        return 'PENDING';
      case 'in_execution':
        return 'RUNNING';
      case 'executed':
        return 'FINISHED';
      default:
        // For any other status, capitalize first letter
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  // Template for status column with colored indicators
  const statusTemplate = (rowData) => {
    return (
      <div className='status-container'>
        <span>{formatStatus(rowData.status)}</span>
      </div>
    );
  };

  // Handle test selection for a specific air product
  const handleTestSelection = (event) => {
    // PrimeReact MultiSelect provides selected values directly in event.value
    setSelectedTests(event.value || []);
  };

  // Handle running the selected tests for a specific air product
  const handleRunTests = () => {
    if (!selectedTests || selectedTests.length === 0) {
      return;
    }

    addAirProductExecution({
      variables: {
        productId: airProduct._id,
        executionData: {
          testNames: selectedTests,
        },
        user: user._id,
      },
    });
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Execution status template
  const executionStatusTemplate = (rowData) => {
    const status = rowData.executionStatus?.toLowerCase() || '';
    let statusClass = 'status-unknown';

    if (status === 'success' || status === 'done')
      statusClass = 'status-success';
    else if (status === 'failure') statusClass = 'status-failed';
    else if (status === 'running') statusClass = 'status-running';

    const statusText =
      AIR_PRODUCT_STATUS_TO_TEXT[status] || rowData.executionStatus || '---';
    return (
      <div
        className={`${statusText} ${statusClass} justify-content-center d-flex`}
      >
        {statusText}
      </div>
    );
  };

  // Open report URL
  const openReportUrl = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Report URL template
  const reportUrlTemplate = (rowData) => {
    const reportUrl = rowData.reportUrl ? (
      <Button
        onClick={() => openReportUrl(rowData.reportUrl)}
        className='job-url-button'
      >
        View Report
      </Button>
    ) : (
      '---'
    );
    const removeExecution = (
      <IoMdTrash
        onClick={() => {
          setSelectedToRemove(rowData.name);
          setShowRemoveExecutionModal(true);
        }}
        className='actions-icon'
      >
        Remove
      </IoMdTrash>
    );
    return (
      <div className='report-url-container'>
        <div className='report-url'>{reportUrl}</div>
        {removeExecution}
      </div>
    );
  };

  const component = (
    <div className='air-products-wrapper'>
      <div className='air-products-container'>
        <h2>{airProduct.name} Air Product</h2>
        <div key={airProduct._id}>
          <div className='air-product-header'>
            <div className='air-product-actions'>
              <div className='test-selection-container'>
                <MultiSelect
                  options={
                    airProduct.tests?.map((test) => ({
                      name: test.name,
                      text: test.name,
                    })) || []
                  }
                  optionLabel='text'
                  optionValue='name'
                  placeholder='Select tests to run'
                  showSelectAll={true}
                  selectAllLabel='Select All'
                  className='w-full'
                  onChange={(e) => handleTestSelection(e)}
                  value={selectedTests}
                />
                <Button
                  onClick={() => handleRunTests(airProduct)}
                  className='run-test-btn'
                  disabled={!selectedTests || selectedTests.length === 0}
                >
                  Run Tests
                </Button>
                {/* {airProduct.jobUrl && (
                    <ButtonComponent
                      onClick={() => openJobUrl(airProduct.jobUrl)}
                      cssClass='job-url-btn'
                    >
                      Job URL
                    </ButtonComponent>
                  )} */}
              </div>
            </div>
          </div>
          <div className='air-product-section'>
            {/* Grid for executions of current air product */}
            <DataTable
              value={airProduct.executions || []}
              paginator
              rows={5}
              sortField='createDate'
              sortOrder={-1}
              emptyMessage='No executions found'
              className='p-datatable-sm'
            >
              <Column
                field='executionStatus'
                header='TestResult'
                className='col-test-result'
                body={executionStatusTemplate}
              />
              <Column
                field='testName'
                header='Test'
                className='col-test-name'
                body={(rowData) => (
                  <div>
                    {Array.isArray(rowData.testName)
                      ? rowData.testName.join(', ')
                      : rowData.testName}
                  </div>
                )}
              />
              <Column
                field='status'
                header='Status'
                className='col-status'
                body={statusTemplate}
              />
              <Column
                field='createDate'
                header='Start Time'
                className='col-date'
                body={(rowData) => (
                  <span>{formatDate(rowData.createDate)}</span>
                )}
              />
              <Column
                field='endDate'
                header='End Time'
                className='col-date'
                body={(rowData) => <span>{formatDate(rowData.endDate)}</span>}
              />
              <Column
                field='reportUrl'
                header='Actions'
                className='col-actions'
                body={reportUrlTemplate}
              />
            </DataTable>
          </div>
        </div>
      </div>
      <CustomModal
        show={showRemoveExecutionModal}
        onClose={() => setShowRemoveExecutionModal(false)}
        onAccept={handleRemoveExecution}
        title='Are you sure you want to delete this execution?'
        acceptButtonText='Delete'
      />
    </div>
  );

  return (
    <GraphQLWrapper loading={loading} error={error} component={component} />
  );
}

export default AirProductsMenu;
